import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';

import { ReactComponent as ProfileIcon } from '../../assets/icons/profile.svg';
import { ReactComponent as RfidIcon } from '../../assets/icons/side-panel/user-side-panel/rfid-icon.svg';
import { useAppDispatch } from '../../redux/hooks';
import { setNotificationDetails } from '../../redux/slice/NotificationSlice';
import ConvertUTCtoDate from '../../shared-functions/ConvertUTCtoDate';
import ConvertUTCtoHHMM from '../../shared-functions/ConvertUTCtoHHMM';
import { FormatUtcString } from '../../shared-functions/FormatUtcString';
import ToFixedValue from '../../shared-functions/ToFixedValue';
import endConsumer from '../../utils/api/endConsumer';
import BatteryLoader from '../battery-loader/BatteryLoader';

interface UserInfo {
  name?: string;
  phoneNumber?: string;
  countryCode?: string;
  email?: string;
  profileImage?: string;
}

interface VehicleInfo {
  manufacturer: string;
  model: string;
  isSelected: boolean;
  connectorType: string;
  batteryCapacity: string;
}

interface SessionInfo {
  startTime: string;
  endTime: string;
  startDate: string;
  endDate: string;
}

interface UserDetailsProperties {
  userInfo: UserInfo;
  rfidNo: string[]; // assuming RFID numbers are strings
  vehicleInfo: VehicleInfo[];
  sessionInfo: SessionInfo[];
  paidAmount: number;
}

interface EndConsumerSidePanelProperties {
  fetchIdFrom: 'queryParams' | 'props';
  id?: string;
}

/**
 * End consumer side panel
 * @returns
 */
export default function EndConsumerSidePanel(properties: EndConsumerSidePanelProperties) {
  const { t } = useTranslation('tableSidepanel');
  const [isLoader, setIsLoader] = useState<boolean>(false);
  const { fetchIdFrom, id } = properties;
  const [searchParameters] = useSearchParams();

  const dispatch = useAppDispatch();

  const [userDetails, setUserDetails] = useState<UserDetailsProperties>({
    userInfo: {},
    rfidNo: [],
    vehicleInfo: [],
    sessionInfo: [],
    paidAmount: 0,
  });

  /**
   * Function to fetch userInfo
   * @param userCustomId
   */
  const handleFetchUserInfo = async (userCustomId: string) => {
    endConsumer
      .getEndConsumerById(userCustomId)
      .then((response) => {
        setUserDetails((previousDetails) => ({
          ...previousDetails,
          userInfo: {
            name: `${response?.data?.firstName || ''} ${response?.data?.lastName || ''}`,
            phoneNumber: response?.data?.phoneNumber || '',
            countryCode: response?.data?.countryCode || '',
            email: response?.data?.email || '',
            profileImage: response?.data?.profileImage?.length
              ? response?.data?.profileImage[0]?.signedUrl
              : '',
          },
        }));
        setIsLoader(false);
      })
      .catch((error) => {
        setIsLoader(false);
        dispatch(
          setNotificationDetails({
            type: 'error',
            notificationDetails: {
              statusCode: error?.response?.data?.code || 200,
              message: error?.response?.data?.message || 'Something went wrong!',
            },
          }),
        );
      });
  };

  /**
   * Function to fetch userInfo
   * @param userCustomId
   */
  const handleTransactionDetails = async (userCustomId: string) => {
    endConsumer
      .fetchTransactionByUserCustomId(userCustomId)
      .then((response) => {
        const paidAmount = response?.data?.reduce(
          (total: any, transaction: any) => total + (Number(transaction?.amount) || 0),
          0,
        );
        setIsLoader(false);

        const sessionInfo: SessionInfo[] = (response?.data || [])?.map((session: any) => ({
          ...session,
          startTime: FormatUtcString(ConvertUTCtoHHMM(session?.transactionStartsAt || '')),
          endTime: FormatUtcString(ConvertUTCtoHHMM(session?.transactionEndsAt || '')),
          startDate: ConvertUTCtoDate(session?.transactionStartsAt || ''),
          endDate: ConvertUTCtoDate(session?.transactionEndsAt || ''),
        }));

        setUserDetails((previousDetails) => ({
          ...previousDetails,
          sessionInfo,
          paidAmount,
        }));
      })
      .catch((error) => {
        setIsLoader(false);
        dispatch(
          setNotificationDetails({
            type: 'error',
            notificationDetails: {
              statusCode: error?.response?.data?.code || 200,
              message: error?.response?.data?.message || 'Something went wrong!',
            },
          }),
        );
      });
  };

  useEffect(() => {
    setIsLoader(true);
    let userCustomId;
    if (fetchIdFrom === 'queryParams') {
      userCustomId = searchParameters.get('userCustomId');
    } else if (fetchIdFrom === 'props') {
      userCustomId = id;
    }

    if (!userCustomId) {
      setIsLoader(false);
      return;
    }

    handleFetchUserInfo(userCustomId);
    handleTransactionDetails(userCustomId);
  }, [searchParameters, id]);

  if (isLoader) {
    return (
      <div className="loader-wrap">
        <div className="loader">
          <BatteryLoader />
        </div>
      </div>
    );
  }

  return (
    <div className="end-consumer-side-panel-wrap">
      <div className="end-consumer-details">
        <div className="end-consumer-profile-content">
          {userDetails.userInfo?.profileImage ? (
            <img src={userDetails.userInfo?.profileImage || ''} alt="" className="profile-image" />
          ) : (
            <ProfileIcon />
          )}
          <div className="end-consumer-content-details">
            <h2>{userDetails.userInfo?.name}</h2>
            <h6>
              {userDetails.userInfo?.email ||
                (userDetails.userInfo?.countryCode
                  ? `${userDetails.userInfo.countryCode} ${userDetails.userInfo.phoneNumber}`
                  : userDetails.userInfo?.phoneNumber)}
            </h6>
            {/* Need to show sts here */}
          </div>
        </div>
        <div className="block-user" />
      </div>
      <div className="user-info-wrap">
        {/* <div className="each-user-info">
          <div className="header-icon-text">
            <span>
              <RfidIcon />
            </span>
            <h6>RFID No</h6>
          </div>
          <p>-</p>
        </div> */}
        {userDetails.userInfo?.phoneNumber && (
          <div className="each-user-info">
            <div className="header-icon-text">
              <span>
                <RfidIcon />
              </span>
              <h6>{t('phoneNumber')}</h6>
            </div>
            <p>
              {userDetails.userInfo?.countryCode
                ? `${userDetails.userInfo.countryCode} ${userDetails.userInfo.phoneNumber}`
                : userDetails.userInfo?.phoneNumber}
            </p>
          </div>
        )}
        {userDetails.userInfo?.email && (
          <div className="each-user-info">
            <div className="header-icon-text">
              <span>
                <RfidIcon />
              </span>
              <h6>{t('email')}</h6>
            </div>
            <p>{userDetails.userInfo?.email}</p>
          </div>
        )}
      </div>
      <div className="session-details-wrap">
        <div className="header-icon-text">
          <span>
            <RfidIcon />
          </span>
          <h6>{t('sessionDetails')}</h6>
        </div>
        <div className="session-details-container">
          {userDetails.sessionInfo?.map((session) => (
            <div className="each-session-details-container">
              <div className="end-consumer-side-panel-card-view">
                <div className="end-consumer-side-panel-card-details">
                  <h6>{t('startTime')}</h6>
                  <p>{session.startTime}</p>
                </div>
              </div>
              <div className="end-consumer-side-panel-card-view">
                <div className="end-consumer-side-panel-card-details">
                  <h6>{t('endTime')}</h6>
                  <p>{session.endTime}</p>
                </div>
              </div>
              <div className="end-consumer-side-panel-card-view">
                <div className="end-consumer-side-panel-card-details">
                  <h6>{t('startDate')}</h6>
                  <p>{session.startDate}</p>
                </div>
              </div>
              <div className="end-consumer-side-panel-card-view">
                <div className="end-consumer-side-panel-card-details">
                  <h6>{t('endDate')}</h6>
                  <p>{session.endDate}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
      <div className="paid-amount-wrap">
        <div className="header-icon-text">
          <span>
            <RfidIcon />
          </span>
          <h6>{t('paidAmount')}</h6>
        </div>
        <p>{ToFixedValue(userDetails.paidAmount)}/-</p>
      </div>
    </div>
  );
}
